import clsx from 'clsx';
import _ from 'lodash';
import { Dispatch, useMemo, useState } from 'react';
import {
  Card,
  CardHeader,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Input,
  Skeleton,
  Text,
} from '@optiaxiom/react';
// TODO: Update to stable Range component when available
import { Range } from '@optiaxiom/react/unstable';

import { InferenceTypeSelector } from '@frontend/components/InferenceTypeSelector';
import { TiptapEditorWrapper } from '@frontend/components/TiptapEditor';
import type { SpecializedAgentAction } from '@frontend/features/agents/hooks';
import { useOpalUser } from '@frontend/hooks';
import * as agentStyles from '@frontend/styles/form.css';
import { SpecializedAgent, Tool } from '@frontend/types';

import { useAgentFormSuggestions } from '../hooks/useAgentFormSuggestions';

import { AgentFormFiles } from './AgentFormFiles';
import { styles } from './AgentFormInput.css';
import { ToolsInput } from './ToolsInput';
import { VariableInput } from './VariableInput';

type AgentFormInputProps = {
  formData: SpecializedAgent;
  dispatch: Dispatch<SpecializedAgentAction>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shouldLoadPromptTemplate: boolean;
  shouldShowUnusedParamsWarning: boolean;
  lockedFields?: string[];
  disabled?: boolean;
  selectedTool: Tool | null;
  onToolSelect: (tool: Tool | null) => void;
};

export function AgentFormInput({
  formData,
  dispatch,
  open,
  onOpenChange,
  shouldLoadPromptTemplate,
  shouldShowUnusedParamsWarning,
  lockedFields = [],
  disabled = false,
  selectedTool,
  onToolSelect,
}: AgentFormInputProps) {
  const parameters = useMemo(
    () => _.sortBy(formData.parameters || [], 'name'),
    [formData.parameters]
  );
  const agentToolNames = useMemo(() => formData.enabled_tools || [], [formData.enabled_tools]);

  const { opalUser } = useOpalUser();
  const [promptError, setPromptError] = useState<string | null>(null);

  const {
    commandExtensions,
    parameterFormOpen,
    setParameterFormOpen,
    editingParameter,
    setEditingParameter,
    toolDialogOpen,
    setToolDialogOpen,
  } = useAgentFormSuggestions({
    parameters,
    agentToolNames,
  });

  return (
    <Flex className={styles.container}>
      <Card bg="bg.secondary" px="8" py="4">
        <Disclosure w="full" onOpenChange={onOpenChange} open={open}>
          <CardHeader>
            <DisclosureTrigger>
              <Text className={styles.fieldTitle}>Input</Text>
            </DisclosureTrigger>
          </CardHeader>
          <DisclosureContent>
            <Flex pt="24" pb="20" px="24">
              <Field
                description="Place dynamic variables in double brackets, eg. [[variable_name]]."
                label="Prompt Template"
                info="Defines the prompt and core logic of this agent. This can include objectives, tasks, and output."
                error={promptError}
                required
                className={agentStyles.FormInputFieldMaxWidth}
              >
                {formData.prompt_template === undefined && shouldLoadPromptTemplate ? (
                  <Skeleton h="56" />
                ) : (
                  <TiptapEditorWrapper
                    key={commandExtensions ? 'with-extensions' : 'loading'}
                    placeholder="Enter your prompt template"
                    className={clsx(
                      styles.promptTemplate,
                      (disabled || lockedFields.includes('prompt_template')) &&
                        styles.promptTemplateDisabled
                    )}
                    value={formData.prompt_template || ''}
                    selectables={parameters || []}
                    usedIn="agent-form"
                    suggestionExtensions={commandExtensions || []}
                    onChange={(value: string) => {
                      dispatch({
                        type: 'UPDATE_FIELD',
                        field: 'prompt_template',
                        value,
                      });
                      if (!value || value.trim() === '') {
                        setPromptError('Prompt Template is required.');
                      } else {
                        setPromptError(null);
                      }
                    }}
                    disabled={disabled || lockedFields.includes('prompt_template')}
                  />
                )}
              </Field>
              <VariableInput
                parameters={parameters}
                dispatch={dispatch}
                promptTemplate={formData.prompt_template}
                shouldShowUnusedParamsWarning={shouldShowUnusedParamsWarning}
                disabled={disabled || lockedFields.includes('variables')}
                parameterFormOpen={parameterFormOpen}
                onParameterFormOpenChange={setParameterFormOpen}
                editingParameter={editingParameter}
                onEditingParameterChange={setEditingParameter}
              />
              <ToolsInput
                agentToolNames={agentToolNames}
                dispatch={dispatch}
                opalInstanceId={opalUser.instance.instance_id}
                disabled={disabled || lockedFields.includes('tools')}
                selectedTool={selectedTool}
                onToolSelect={onToolSelect}
                toolDialogOpen={toolDialogOpen}
                onToolDialogOpenChange={setToolDialogOpen}
              />
              <Field
                label="Inference Level"
                info="Controls how much reasoning Opal applies when generating its response. Higher inference levels can take more time, but apply deeper reasoning to output"
              >
                <InferenceTypeSelector
                  value={formData.inference_type}
                  onChange={inference_type =>
                    dispatch({
                      type: 'UPDATE_FIELD',
                      field: 'inference_type',
                      value: inference_type,
                    })
                  }
                  disabled={disabled || lockedFields.includes('inference')}
                />
              </Field>
              <Field
                label="Creativity"
                info="(0-1 scale) controls the randomness of Opal’s output and how creative it should be. Lower values produce more predictable results, while higher values permit more variety."
              >
                <Flex flexDirection="row" gap="8">
                  <Range
                    min={0.1}
                    max={1.0}
                    step={0.1}
                    value={formData.creativity || 0.1}
                    onValueChange={(value: number) =>
                      dispatch({ type: 'UPDATE_FIELD', field: 'creativity', value })
                    }
                    disabled={disabled || lockedFields.includes('creativity')}
                    showTooltip
                    maxW="xs"
                  />
                  <Input
                    placeholder="0"
                    value={formData.creativity?.toString() || '0.1'}
                    onChange={e => {
                      const value = parseFloat(e.target.value) || 0.1;
                      const clampedValue = Math.max(0.1, Math.min(1, value));
                      dispatch({ type: 'UPDATE_FIELD', field: 'creativity', value: clampedValue });
                    }}
                    type="number"
                    min="0.1"
                    max="1.0"
                    step="0.1"
                    disabled={disabled || lockedFields.includes('creativity')}
                  />
                </Flex>
              </Field>
              <Field label="Files" info="Attach reference files for your agent here">
                <AgentFormFiles
                  formData={formData}
                  dispatch={dispatch}
                  disabled={disabled || lockedFields.includes('files')}
                />
              </Field>
            </Flex>
          </DisclosureContent>
        </Disclosure>
      </Card>
    </Flex>
  );
}
